import { queryKeys } from 'hooks/queryKeys'
import { PageMeta } from 'components/PageMeta'
import { Borrow } from 'components/sto/Borrow'
import { BaseLayout } from 'components/layouts'
import { Support } from 'components/sto/Support'
import { Trading } from 'components/sto/Trading'
import { Updates } from 'components/sto/Updates'
import { Documents } from 'components/sto/Documents'
import { KeyTerms } from 'components/sto/HeroMetrics'
import { KeyActions } from 'components/sto/KeyActions'
import { useStoAnchors } from 'hooks/sto/useStoAnchors'
import { useStoProject } from 'hooks/sto/useStoProject'
import { Performance } from 'components/sto/Performance'
import { HowToInvest } from 'components/sto/HowToInvest'
import { stoQueries } from 'hooks/sto/queries/stoQueries'
import { DisclaimerBar } from 'components/ui/disclaimerBar'
import { useLocation, useNavigate } from 'react-router-dom'
import { useStoPageTracking } from 'hooks/sto/useStoTracking'
import { KeyInformation } from 'components/sto/KeyInformation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ProductOverview } from 'components/sto/ProductOverview'
import { DisplayOnBreakpoint, t } from '@stokr/components-library'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { Dividends, NextDividend } from 'components/sto/Dividends'
import { buildIssuerAttribution } from 'hooks/sto/issuerAttribution'
import { StoPageActionsProvider } from 'context/StoPageActionsContext'
import { NewVentureModal, ProfessionalInvestorModal } from 'shared/components'
import { MODAL_TYPES, type ModalType } from 'shared/domain/projectEligibility'
import { ImportantConsiderations } from 'components/sto/ImportantConsiderations'
import { useAuth, useCloseOverlaysOnSessionExpiry } from 'shared/context/AuthContext'
import { EligibilityModal } from 'components/modals/EligibilityModal/EligibilityModal'
import { buildHeroIdentity, toBreadcrumbStructuredData } from 'hooks/sto/useHeroIdentity'
import { Hero, HeroBreadcrumb, InvestDisclaimer, StickyHeader } from 'components/sto/Hero'
import { Band, BandColumn, BandGrid, BandInner, HeroAside, InvestGroup } from './StoPage.styles'
import { hasEligibilityConfirmation } from 'components/modals/EligibilityModal/eligibilityConfirmation'

/**
 * STO details page.
 *
 * Composition only — every section owns its own hook and data. Sections are listed
 * explicitly here rather than driven by config: reordering or removing one is a line
 * in this file plus its own folder. See docs/STO-ARCHITECTURE.md.
 *
 * Exactly one PageMeta per page (React 19 does not dedupe <title>).
 */
const StoPage = () => {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { slug, project, isNotFound } = useStoProject()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Issuer entity, falling back to the company entry — same as `project.issuer ?? project.company` before.
  const companyQuery = useQuery(stoQueries.company(project?.company ?? undefined))
  const issuerQuery = useQuery(stoQueries.company(project?.issuer ?? undefined))
  const { issuerName, issuerDetails } = buildIssuerAttribution(issuerQuery.data ?? companyQuery.data)
  useStoAnchors()
  useStoPageTracking()
  // Held here rather than in a section: Documents gating and KeyActions both need it.
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  // The modal completing an eligibility step: private or EU professional investor.
  const [stepModal, setStepModal] = useState<ModalType | null>(null)
  const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false)

  // As the legacy page: a slug the allowlist knows but the API does not return goes to the 404 page
  // (the router only 404s a slug unknown to both), instead of a skeleton that never resolves.
  useEffect(() => {
    if (isNotFound) navigate('/page-not-found', { replace: true })
  }, [isNotFound, navigate])

  // The eligibility gate, shared with featured-assets: a confirmation made there suppresses it here.
  useEffect(() => {
    if (!hasEligibilityConfirmation()) setIsEligibilityModalOpen(true)
  }, [])

  // Tabs are gone: every tab of a project now collapses to one canonical URL.
  const canonical = `/${slug}`
  const identity = buildHeroIdentity(project)

  // Sections (How can I invest?) open the login modal in place rather than sending the
  // visitor to the homepage to sign in.
  const openLoginModal = useCallback(() => setIsLoginModalOpen(true), [])
  const openStepModal = useCallback((modal: ModalType) => setStepModal(modal), [])
  const closeStepModal = useCallback(() => setStepModal(null), [])
  // Same as the legacy page: an expired session must not leave a modal open over the page.
  const closePageModals = useCallback(() => {
    setStepModal(null)
    setIsLoginModalOpen(false)
  }, [])
  useCloseOverlaysOnSessionExpiry(closePageModals)

  // Only on a real success (the modal has no response-data callback to update the cache
  // surgically), so a refetch, not a failed/cancelled close.
  const handlePrivateInvestorSuccess = useCallback(() => {
    if (user?._id) queryClient.invalidateQueries({ queryKey: queryKeys.privateInvestorAll(user._id) })
    setStepModal(null)
  }, [queryClient, user])

  return (
    <BaseLayout user={user} isLoginModalOpen={isLoginModalOpen} setIsLoginModalOpen={setIsLoginModalOpen}>
      <StoPageActionsProvider openLoginModal={openLoginModal} openStepModal={openStepModal}>
        {/* Keyed by product: the route reuses this page across products, and no section's
            local state (chart range, open panels) belongs to the next one. */}
        <Fragment key={slug}>
          <PageMeta
            title={project?.title ?? project?.name}
            // The short excerpt, as the hero shows it: search results cut a description at ~155 characters.
            description={identity?.description}
            image={project?.coverimage || undefined}
            path={pathname}
            canonical={canonical}
          />
          {identity && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: toBreadcrumbStructuredData(identity.breadcrumb, canonical) }}
            />
          )}

          <StickyHeader />

          <Band>
            <BandInner $compactTop>
              <HeroBreadcrumb />
              <BandGrid>
                <BandColumn $gap={10}>
                  <Hero />
                </BandColumn>
                <HeroAside $gap={4}>
                  <NextDividend placement="aside" />
                  <KeyActions />
                </HeroAside>
              </BandGrid>
            </BandInner>
          </Band>

          {/* Figma 3329:24560: Get support moved to the main column, under Dividends. */}
          <Band>
            <BandInner>
              <BandGrid>
                <BandColumn $gap={10} $hideEmpty>
                  {/* Mobile: How can I invest? opens this band, the hero's disclaimer under it. */}
                  <DisplayOnBreakpoint down breakpoint="MLarge">
                    <InvestGroup>
                      <HowToInvest mirror />
                      <InvestDisclaimer />
                    </InvestGroup>
                  </DisplayOnBreakpoint>
                  <ProductOverview />
                  <KeyTerms />
                  <Performance />
                  <Dividends />
                  <DisplayOnBreakpoint up breakpoint="MLarge">
                    <Support />
                  </DisplayOnBreakpoint>
                </BandColumn>
                <BandColumn $gap={8} $hideEmpty>
                  <DisplayOnBreakpoint up breakpoint="MLarge">
                    <HowToInvest />
                  </DisplayOnBreakpoint>
                  <KeyInformation />
                  {/* Mobile: Get support after Key information. */}
                  <DisplayOnBreakpoint down breakpoint="MLarge">
                    <Support mirror />
                  </DisplayOnBreakpoint>
                </BandColumn>
              </BandGrid>
            </BandInner>
          </Band>

          {/* Figma 3329:25091: full-width cards, 32px apart, Trading first. Borrow is not in the frame. */}
          <Band>
            <BandInner>
              <BandColumn $gap={8}>
                <Trading />
                <Borrow />
                <Updates />
                <Documents />
                <ImportantConsiderations />
              </BandColumn>
            </BandInner>
          </Band>
        </Fragment>

        {/* Full-bleed, below the bands — same placement as the legacy page. */}
        <DisclaimerBar
          paragraphs={[
            {
              labelId: 'common.issuerAttributionLabel',
              textId: 'common.issuerAttributionText',
              values: { issuerName, issuerDetails },
            },
            { labelId: 'common.riskWarningLabel', textId: 'common.riskWarningText' },
            { labelId: 'common.noAdviceStatementLabel', textId: 'common.noAdviceStatementText' },
          ]}
        />

        {project && (
          <NewVentureModal
            title={project.title}
            description={t('sto.newVentureModal.description')}
            isModalOpen={stepModal === MODAL_TYPES.PRIVATE_INVESTOR}
            project={project}
            onModalClose={closeStepModal}
            onFormSend={async () => {}}
            onSuccessMessageBtnClick={handlePrivateInvestorSuccess}
          />
        )}
        {project && (
          <ProfessionalInvestorModal
            isModalOpen={stepModal === MODAL_TYPES.EU_PROFESSIONAL_INVESTOR}
            onModalClose={closeStepModal}
            tokenSymbol={project.tokenSymbol ?? undefined}
            userType={user?.user_type}
          />
        )}
        {isEligibilityModalOpen && (
          <EligibilityModal
            isOpen={isEligibilityModalOpen}
            onConfirm={() => setIsEligibilityModalOpen(false)}
            page="sto"
            projectName={project?.name}
          />
        )}
      </StoPageActionsProvider>
    </BaseLayout>
  )
}

export default StoPage
